// Simulation de forces — placement du graphe de similarité.
//
// Portage de poc/src/force.js ; tests/parite-poc.test.ts vérifie que les deux
// rendent les mêmes positions. Les réglages (rappel 0,08, cinq passes de
// collision, ressort 5, amortissement ≤ 0,6…) y sont justifiés par des mesures :
// les changer ici sans refaire ces mesures casserait la parité et la lisibilité.
//
// Deux propriétés tenues volontairement :
//   · déterministe — spirale de départ et bruit à graine fixe : deux ouvertures
//     donnent le même dessin, ce qui compte pour présenter et désigner un nœud ;
//   · bornée — le rappel vers le centre retient les projets sans arête.
//
// Répulsion naïve en O(n²) : quelques dizaines de nœuds, Barnes-Hut n'apporterait rien.

export const ALPHA_MIN = 0.001;
const ALPHA_DECROISSANCE = 1 - Math.pow(ALPHA_MIN, 1 / 300); // ≈ 300 pas pour converger
const ALPHA_MONTEE = 0.25;
const AMORTISSEMENT = 0.6;
const RECENTRAGE = 0.15;
const GRAINE = 20260915;

export interface Noeud {
  x: number;
  y: number;
  vx: number;
  vy: number;
  fx: number | null;
  fy: number | null;
}

export interface LienEntree {
  source: number;
  cible: number;
  poids: number;
}

export interface OptionsSimulation {
  largeur?: number;
  hauteur?: number;
  rayon?: number;
  /** Demi-encombrement d'un nœud (disque + acronyme) : la collision est elliptique. */
  rayonX?: number;
  rayonY?: number;
  distanceMin?: number;
  distanceMax?: number;
  repulsion?: number;
  rappel?: number;
  amortissement?: number;
  forceLien?: number;
  forceCollision?: number;
  passesCollision?: number;
  repulsionPlancher?: number;
}

export interface Boite {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  largeur: number;
  hauteur: number;
}

/** Générateur congruentiel : du bruit reproductible, contrairement à Math.random. */
function alea(graine: number): () => number {
  let etat = graine || 1;
  return () => {
    etat = (etat * 1664525 + 1013904223) % 4294967296;
    return etat / 4294967296;
  };
}

/** Spirale de phyllotaxie : aucun nœud superposé au départ, sinon la répulsion diverge. */
function placerEnSpirale(noeuds: Noeud[], cx: number, cy: number): void {
  const PHI = Math.PI * (3 - Math.sqrt(5));
  noeuds.forEach((n, i) => {
    const rayon = 26 * Math.sqrt(i + 0.5);
    const angle = i * PHI;
    n.x = cx + rayon * Math.cos(angle);
    n.y = cy + rayon * Math.sin(angle);
    n.vx = 0;
    n.vy = 0;
    n.fx = null;
    n.fy = null;
  });
}

/** Espacement tel que n disques occupent ~85 % du plus petit côté du cadre. */
export function espacementNaturel(n: number, largeur: number, hauteur: number): number {
  if (n < 2) return 40;
  return Math.max(14, (0.55 * Math.min(largeur, hauteur)) / (2 * 1.1 * Math.sqrt(n)));
}

const estEpingle = (n: Noeud) => n.fx !== null || n.fy !== null;

export function simulation(nombre: number, liensEntree: LienEntree[], o: OptionsSimulation = {}) {
  let largeur = o.largeur || 800;
  let hauteur = o.hauteur || 600;
  let cx = largeur / 2;
  let cy = hauteur / 2;
  const rayonX = o.rayonX || o.rayon || espacementNaturel(nombre, largeur, hauteur);
  const rayonY = o.rayonY || o.rayon || rayonX;
  const rayon = Math.sqrt(rayonX * rayonY);

  const repulsion = o.repulsion ?? -520;
  const distanceMin = o.distanceMin || rayon * 1.4;
  const distanceMax = o.distanceMax || rayon * 3.4;
  const rappel = o.rappel ?? 0.08;
  // Ne pas dépasser 0,6 : l'intégrateur de Verlet diverge au-delà.
  const amortissement = o.amortissement ?? AMORTISSEMENT;
  const forceLien = o.forceLien ?? 5;
  const forceCollision = o.forceCollision ?? 0.5;
  const passesCollision = o.passesCollision ?? 5;
  const plancher = (o.repulsionPlancher || 24) * (o.repulsionPlancher || 24);

  const noeuds: Noeud[] = Array.from({ length: nombre }, () => ({
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    fx: null,
    fy: null,
  }));
  const bruit = alea(GRAINE);
  let alpha = 1;
  let cible = 0;

  placerEnSpirale(noeuds, cx, cy);

  // Un nœud très connecté bouge moins qu'une feuille (biais de d3-force).
  const degre = noeuds.map(() => 0);
  for (const l of liensEntree) {
    degre[l.source] = (degre[l.source] ?? 0) + 1;
    degre[l.cible] = (degre[l.cible] ?? 0) + 1;
  }

  // Plus deux projets se ressemblent, plus on les rapproche ; la plage suit les
  // arêtes retenues, pas [0, 1].
  const scores = liensEntree.map((l) => l.poids);
  const pMin = scores.length ? Math.min(...scores) : 0;
  const pMax = scores.length ? Math.max(...scores) : 1;
  const liens = liensEntree.map((l) => {
    const s = noeuds[l.source];
    const c = noeuds[l.cible];
    if (!s || !c) throw new RangeError(`lien ${l.source}–${l.cible} hors des ${nombre} nœuds`);
    const t = pMax > pMin ? (l.poids - pMin) / (pMax - pMin) : 0.5;
    const ds = degre[l.source] ?? 0;
    const dc = degre[l.cible] ?? 0;
    return {
      s,
      c,
      distance: distanceMax - (distanceMax - distanceMin) * t,
      force: forceLien / Math.min(ds, dc),
      biais: ds / (ds + dc),
    };
  });

  const fremir = () => (bruit() - 0.5) * 1e-6;

  function forceLiens() {
    for (const { s, c, ...l } of liens) {
      let dx = c.x + c.vx - s.x - s.vx || fremir();
      let dy = c.y + c.vy - s.y - s.vy || fremir();
      const d = Math.sqrt(dx * dx + dy * dy);
      const k = ((d - l.distance) / d) * alpha * l.force;
      dx *= k;
      dy *= k;
      c.vx -= dx * l.biais;
      c.vy -= dy * l.biais;
      s.vx += dx * (1 - l.biais);
      s.vy += dy * (1 - l.biais);
    }
  }

  function forceRepulsion() {
    for (let i = 0; i < noeuds.length; i++) {
      const a = noeuds[i];
      for (let j = i + 1; a && j < noeuds.length; j++) {
        const b = noeuds[j];
        if (!b) continue;
        const dx = b.x - a.x || fremir();
        const dy = b.y - a.y || fremir();
        let d2 = dx * dx + dy * dy;
        if (d2 < plancher) d2 = plancher;
        const k = (repulsion * alpha) / d2;
        const fx = dx * k;
        const fy = dy * k;
        b.vx += fx;
        b.vy += fy;
        a.vx -= fx;
        a.vy -= fy;
      }
    }
  }

  function forceRappel() {
    for (const n of noeuds) {
      n.vx += (cx - n.x) * rappel * alpha;
      n.vy += (cy - n.y) * rappel * alpha;
    }
  }

  // Les encombrements ne se chevauchent pas : tous les acronymes restent lisibles
  // en permanence. Test fait dans le repère où l'ellipse est un cercle.
  function forceCollisions() {
    for (let passe = 0; passe < passesCollision; passe++) {
      for (let i = 0; i < noeuds.length; i++) {
        const a = noeuds[i];
        for (let j = i + 1; a && j < noeuds.length; j++) {
          const b = noeuds[j];
          if (!b) continue;
          const dx = b.x + b.vx - a.x - a.vx || fremir();
          const dy = b.y + b.vy - a.y - a.vy || fremir();
          const nx = dx / (2 * rayonX);
          const ny = dy / (2 * rayonY);
          const d2 = nx * nx + ny * ny;
          if (d2 >= 1) continue;
          const d = Math.sqrt(d2) || 1e-6;
          const k = ((1 - d) / d) * forceCollision;
          const fx = dx * k;
          const fy = dy * k;
          b.vx += fx;
          b.vy += fy;
          a.vx -= fx;
          a.vy -= fy;
        }
      }
    }
  }

  function pas(): number {
    alpha += (cible - alpha) * (cible > alpha ? ALPHA_MONTEE : ALPHA_DECROISSANCE);
    forceLiens();
    forceRepulsion();
    forceRappel();
    forceCollisions();
    let sx = 0;
    let sy = 0;
    let libres = 0;
    for (const n of noeuds) {
      if (n.fx !== null) {
        n.x = n.fx;
        n.vx = 0;
      } else n.x += n.vx *= amortissement;
      if (n.fy !== null) {
        n.y = n.fy;
        n.vy = 0;
      } else n.y += n.vy *= amortissement;
      if (!estEpingle(n)) {
        sx += n.x;
        sy += n.y;
        libres++;
      }
    }
    // Recentrage partiel, qui ignore les nœuds épinglés (sinon le nœud tenu vibre).
    if (libres) {
      const dx = (cx - sx / libres) * RECENTRAGE;
      const dy = (cy - sy / libres) * RECENTRAGE;
      for (const m of noeuds) {
        if (estEpingle(m)) continue;
        m.x += dx;
        m.y += dy;
      }
    }
    return alpha;
  }

  return {
    noeuds: () => noeuds,
    alpha: () => alpha,
    pas,
    /** Refroidit jusqu'à convergence d'un coup : la vue s'ouvre sur un graphe posé. */
    stabiliser(maximum = 400): number {
      let n = maximum;
      while (alpha > ALPHA_MIN && n-- > 0) pas();
      return alpha;
    },
    relancer(chaleur = 0.6): number {
      alpha = chaleur;
      return alpha;
    },
    /** Température visée : tiède pendant un glissement, puis refroidissement. */
    cibler(temperature = 0): number {
      cible = temperature;
      return cible;
    },
    /** Épingle un nœud tout de suite, sans attendre le pas suivant. */
    fixer(i: number, x: number, y: number): void {
      const n = noeuds[i];
      if (!n) return;
      n.fx = x;
      n.fy = y;
      n.x = x;
      n.y = y;
      n.vx = 0;
      n.vy = 0;
    },
    liberer(i: number): void {
      const n = noeuds[i];
      if (!n) return;
      n.fx = null;
      n.fy = null;
    },
    dimensionner(l: number, h: number): void {
      largeur = l;
      hauteur = h;
      cx = l / 2;
      cy = h / 2;
    },
    boite(): Boite {
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      for (const n of noeuds) {
        x0 = Math.min(x0, n.x);
        x1 = Math.max(x1, n.x);
        y0 = Math.min(y0, n.y);
        y1 = Math.max(y1, n.y);
      }
      return { x0, y0, x1, y1, largeur: x1 - x0, hauteur: y1 - y0 };
    },
  };
}

export type Simulation = ReturnType<typeof simulation>;
